export const en = {
  translation: {
    shared: {
      cancel: 'Cancel',
      edit: 'Edit',
      save: 'Save',
      create: 'Create',
      saveChanges: 'Save changes',
      unsavedChangesWarning: 'You have unsaved changes',
      unsavedChangesQuestion: 'Do you want to discard your changes?',
      unsavedEditDescription: `You haven't saved your changes to the notification rule yet. If you close now, all your edits will be lost.`,
      close: 'Close',
      closeWithoutSaving: 'Close without saving',
      discard: 'Discard',
      clear: 'Clear',
      noDataToShow: 'No data to show',
      continue: 'Continue',
      continueEdit: 'Continue editing',
      logout: 'Logout',
      status: 'Status',
      actions: 'Actions',
      delete: 'Delete',
      intlDateTime: '{{val, dateTime}}',
      email: 'Email',
      back: 'Back',
      tryAgain: 'Try again',
      loading: 'Loading...',
      later: 'Later',
      updateNow: 'Update Now',
      verifyNumber: 'Verify number',
      viewDeviceEvents: 'View device events',
      tryAdjustSearchCriteria:
        'Please try adjusting your search criteria or check spelling (search is case-sensitive).',
      hour: 'hour',
      hours: 'hours',
      min: 'min',
      unknown: 'Unknown',
      error: 'Error',
      viewMonitoringDetails: 'Monitoring',
      unknownError: 'Unknown error',
      incident: 'Incident',
      incidentDetails: 'Incident details',
      notificationRule: 'Notification rule',
      recipients: 'Recipients',
    },
    validationMessages: {
      required: 'This field can not be empty.',
      maxLength120: 'Field must not exceed 120 characters.',
      searchMaxLength120: 'You have reached the search term limit of 120 characters.',
      maxLength2500: 'Field must not exceed 2500 characters.',
      phoneNumberRequired: 'Phone number is required',
      invalidPhoneNumber:
        'White spaces, leading zeros, and special characters are not allowed.',
      countryCodeRequired: 'Country code is required',
      phoneNumberMinLength: 'Phone number must be at least 5 digits long',
    },
    errors: {
      oops: 'Oops!',
      unexpected: 'Sorry, an unexpected error has occurred.',
      unauthorized: 'You are unauthorized to execute this actions.',
      informationCouldNotBeLoaded: 'Information could not be loaded',
      contactSupport: 'If the problem persists',
      contactSupport2: 'consult the VisioNize AI assistant.',
      errorOccurred: 'An error occurred',
    },
    serialNumbers: {
      enter: 'Enter the serial number',
      submit: 'Submit serial number',
      validationMessage: 'This does not seem to be a correct serial number.',
      serialNumberDuplicateErrorMessage:
        'A device with this serial number has been added previously. Please check if the serial number for the device you try to add is correct.',
    },
    devices: {
      overview: 'Device overview',
      serialNumber: 'Serial number',
      searchPlaceholder: 'Search devices',
      emptySearch: 'No devices found',
      inventoryId: 'Inventory ID',
    },
    addDevice: {
      add: 'Add device',
      connectable: 'Connectable',
      notConnectable: 'Not connectable',
      isAdded: 'Device added',
      selectDeviceTypeInfo:
        'To add a new device, please select one of the following options to guide you through the process.',
      connectableDevice: 'Connectable device',
      waiting: 'Waiting for connection',
      waitingInfo: 'Ensure your device is switched on and connected to the network.',
      waitingInfoSenseMonitor:
        'Ensure your Sense Gateway is registered in VisioNize. It needs to be near your Sense Monitor. Please check that the device is switched on and connected to the network.',
      addError: 'Registration could not be completed',
      addErrorInfo:
        'The registration process for the following device could not be completed.',
      successInfo:
        '{{type}} with the serial number <strong>{{serialNumber}}</strong> was successfully added. You can now start monitoring your device or setup a notification rule for it.',
      retryRegistration: {
        hint: 'Device registration process could not be completed.',
        pleaseWait: 'Registration process running. Please wait.',
      },
    },
    removeDevice: {
      title: 'Delete the device?',
      successDialog: {
        title: 'Device deleted',
        message: 'The device was successfully deleted.',
      },
      confirmDeleteDescription: {
        top: 'If you delete the device:',
        gateway: {
          bullet1:
            'All device data, including measurement, events, and metadata, will become unavailable.',
          bullet2: 'Users will stop receiving notifications for this device.',
          bullet3: 'Devices associated with this gateway may no longer be connected.',
          bullet4: 'Audit trail data will not be affected.',
        },
        monitor: {
          bullet1:
            'All device data, including measurement, events, and metadata, will become unavailable.',
          bullet2: 'Users will stop receiving notifications for this device.',
          bullet3: 'Audit trail data will not be affected.',
          bullet4: '',
        },
        box: {
          bullet1:
            'All device data, including measurement, events, and metadata, will become unavailable.',
          bullet2: 'Users will stop receiving notifications for this device.',
          bullet3: 'Devices associated with this box may no longer be connected.',
          bullet4: 'Audit trail data will not be affected.',
        },
        generic: {
          bullet1:
            'All device data, including measurement, events, and metadata, will become unavailable.',
          bullet2: 'Users will stop receiving notifications for this device.',
          bullet3: 'Audit trail data will not be affected.',
          bullet4: '',
        },
      },
      confirm: 'Remove device',
    },
    deviceType: {
      freezer: 'Freezer',
      incubator: 'Incubator',
      monitor: 'Monitor',
      gateway: 'Gateway',
      'centrifuge-large': 'Centrifuge',
      'sense-monitor': 'Sensor',
      'sense-gateway': 'Gateway',
      'vn-box': 'VisioNize box',
      unknown: 'Unknown',
      thermomixer: 'Thermomixer',
      'generic-device': 'Generic device',
    },
    deviceTable: {
      pairNow: 'Pair now',
      type: 'Device type',
      name: 'Device name',
      manufacturer: 'Manufacturer',
      model: 'Device model',
      emptyState: 'No devices',
      emptyStateHint: 'Add your first device to start monitoring your lab in real time',
    },
    deviceConnectionStatus: {
      pending: 'Pairing pending',
      waitingForMetadata: 'Pending',
      paired: 'Connected',
      readyToPair: 'Ready to pair',
    },
    deviceDetail: {
      fallbackHeadlineNameEmpty: 'Device',
      tabs: {
        details: 'Details',
        notes: 'Notes',
        associatedRouter: 'Associated Router',
        associatedRouterShort: 'Router',
        associatedMonitors: 'Associated Monitors',
        associatedMonitorsShort: 'Monitors',
        associatedDevices: 'Associated Devices',
        associatedDevicesShort: 'Devices',
        associatedVnBox: 'Associated VN Box',
        associatedVnBoxShort: 'VN Box',
        associatedMonitorsDialog: {
          blockedTitle: 'Monitor blocked',
          unblockedTitle: 'Monitor unblocked',
          blockedDescription:
            'The Visionize Sense environment monitor  <bold>{{associatedMonitorName}}</bold> is now blocked from connecting to the Visionize Sense gateway router <bold>{{gatewayName}}.</bold>',
          unblockedDescription:
            'The Visionize Sense environment monitor  <bold>{{associatedMonitorName}}</bold> is now unblocked from connecting to the Visionize Sense gateway router <bold>{{gatewayName}}.</bold>',
          disclaimer: 'The change can take some minutes to reflect in connection status.',
        },
      },
      deviceInformation: {
        generalInformation: 'General Information',
        location: 'Location',
        address: 'Address',
        site: 'Site',
        building: 'Building',
        room: 'Room',
        position: 'Position',
        country: 'Country',
        countrySelectPlaceHolder: 'Select a country',
        noCountryOptions: 'No countries to select',
        countryISOCodeNotFound: '{{code}} (Country ISO code not found)',
        street: 'Street',
        postcode: 'Postcode',
        city: 'City',
        state: 'State',
        company: 'Company',
        deviceType: 'Device type',
        deviceName: 'Device name',
        serialNumber: 'Serial number',
        customInventoryId: 'Custom inventory ID',
        manufacturer: 'Manufacturer',
        deviceModel: 'Device model',
        notes: 'Notes',
        softwareVersion: 'Software version',
        pairingInformation: 'Pairing Information',
        updateAvailable: 'Update available',
        updateGatewayDialog: {
          title: 'Software update',
          description:
            'Updates for the following <bold>VisioNize Sense gateway router</bold> are available:',
          disclaimer:
            '<bold>Note</bold>: Updating the gateway router might affect the <bold>VisioNize sense sensors</bold> device as well. Please check the release notes for further details',
        },
        updateDialog: {
          title: 'Software update',
          description:
            'Update to version <bold>{{version}}</bold> is available for the following <bold>{{deviceType}}</bold>:',
          descriptionCopla:
            'Update to version <bold>{{version}}</bold> is available. Please perform the update directly on your device.',
          disclaimer:
            '<bold>Note</bold>: Updating the gateway router might affect the <bold>connected</bold> devices as well. Please check the release notes for further details',
        },
        updatePublishedDialog: {
          title: 'Software update published',
          disclaimer: 'The latest available version is <bold>{{version}}.</bold>',
        },
      },
      associatedDevices: {
        emptyTitle: 'No devices connected',
        emptyMessage: 'No associated devices.',
      },
      boxConnectedDevicesTable: {
        connection: 'Connection',
        devices: 'Devices',
        emptyTitle: 'No devices connected',
        emptyMessage:
          'Connect devices to the box for real-time monitoring and alerts. See the VisioNize Box manual for instructions and compatibility.',
      },
      associatedBoxTable: {
        connection: 'Connection',
        devices: 'VN Box',
        emptyTitle: 'No VN box connected',
        emptyMessage:
          'Please ensure that the connection between the device and the VN box is established. See the VisioNize Box manual for more details.',
      },
      routerTable: {
        connection: 'Connection',
        signal: 'Signal',
        router: 'Gateway router',
        emptyMessage:
          'No router associated. Please make sure a Visionize Sense gateway router is switched on and in the vicinity of your sensor.',
      },
      monitorsTable: {
        connection: 'Connection',
        monitors: 'Monitors',
        block: 'Block',
        emptyMessage:
          'No monitor associated. Please make sure a Visionize Sense monitor is switched on and in the vicinity of your router.',
      },
      routerStatus: {
        stable: 'Stable',
        unstable: 'Unstable',
        noConnection: 'Not connected',
        blocked: 'Blocked',
      },
    },
    pairing: {
      success: 'You can now use all features of VisioNize with this device.',
    },
    deviceModels: {
      F740: 'Cyrocube® F740 Freezer',
    },
    monitoring: {
      overview: 'Monitoring',
      activityStatus: 'Activity status',
      device: 'Device',
      alertStatus: 'Alert status',
      doorOrLid: 'Door/Lid',
      actions: 'Actions',
      emptyState: 'No devices connected',
      emptyStateHint:
        'Either add a device or check the device connectivity to start monitoring',
      gotoDeviceOverview: 'Go to device overview',
      report: {
        viewAllReports: 'All reports',
        createButton: 'Create report',
        dialog: {
          title: 'New stability report',
          timespan: 'Time range',
          startTime: 'Start time',
          endTime: 'End time',
          parameters: 'Parameters',
          selectAll: 'Select all',
          clearAll: 'Clear all',
          loadingParameters: 'Loading parameters...',
          parametersLoadError: 'Parameters could not be loaded. Please try again.',
          noParameters: 'Parameters are unavailable for this device.',
          invalidPeriod:
            'Select a valid period within the available history, ending no later than now.',
          saveError:
            'The report could not be created. Check the selected parameters and try again.',
          selectOperator: 'Select operator',
          notes: 'Notes',
          infoMessage:
            'Stability reports capture device data over a specified time range, including <bold>charts, event data</bold>, and <bold>parameter statistics</bold>. Once created, the record will be available in the <bold>Report hub.</bold>',
        },
        success: {
          title: 'Report generation in process',
          description:
            "This may take a few moments. You can view and download it in the Report hub once it's ready.",
          goToReportHub: 'Go to report hub',
        },
      },
    },
    monitoringDetail: {
      parameters: 'Parameters',
      'history-charts': 'History charts',
      'history-charts-short': 'History',
      events: 'Events',
      export: 'Export',
      exportDetail: {
        title: 'Export your weekly data',
        hint: 'CSV reports will be generated for all process value measures <br/>from the history charts and for all device events.',
        note: '<bold>Please note:</bold> data export is only available for the last seven days.',
        buttonText: 'Download as ZIP',
        errorMessage: 'Error downloading report. Please try again.',
      },
      emailExportDetail: {
        title: 'Export monitoring data',
        hint: 'CSV reports will be generated for all process value measures <br/>from the history charts and for all device events.',
        note: '<bold>Please note:</bold> export file will be emailed to you once fully generated.',
        buttonText: 'Generate export',
        last7Days: 'Last 7 days',
        lastXDays: 'Last {{days}} days',
        errorMessage: 'Error downloading report. Please try again.',
        successMessage: 'Report generation in process',
        successMessageHint:
          'This may take a few moments. Once completed, you will receive an email to <bold>{{email}}</bold> with a download link.',
        limitSubscriptionBannerHeader: 'Data available: Last 7 days',
        limitSubscriptionBannerText:
          'Access up to 90 days of data with a Sample Safety+ plan. Contact your administrator to upgrade, or <l>reach out to us</l> for assistance.',
        customRangeTitle: '[SupportUser] Custom date range',
        customRangeButtonText: 'Generate custom range',
      },
      historyChartsDetail: {
        deviceNotConfigured: 'Device not configured to show history charts.',
        showAlertThresholdsToggleLabel: 'Setpoint and thresholds',
        showSetpointToggleLabel: 'Setpoint',
        timezoneHint: 'The data is shown in your local timezone',
        noDataAvailable: 'No data available',
        noChartData: 'No chart data within the chosen time range',
        highLowAlertIndicator: 'High/Low alert',
        setPointIndicator: 'Setpoint',
        processValueIndicator: 'Process value',
        highAlertLabel: 'High alert',
        lowAlertLabel: 'Low alert',
        setPointLabel: 'Setpoint',
        minValueLabel: 'Min.',
        maxValueLabel: 'Max.',
        rangePeriod: {
          custom: 'Custom',
          '30days': '30 days',
          '14days': '14 days',
          '7days': '7 days',
          '24hours': '24 hours',
          live: 'Live',
        },
      },
      eventsDetail: {
        type: 'Type',
        time: 'Time',
        event: 'Event',
        category: 'Category',
        eventSearchInputPlaceholder: 'Search for event',
        timezoneHint: 'Events are shown in your local timezone',
        newEvents: 'New events',
        categories: {
          'NO CATEGORY': 'No category',
          CONNECTIVITY: 'Connectivity',
          reconnected: 'Reconnected',
          'DOOR ACCESS': 'Door access',
          'door opened': 'Door opened',
          'door closed': 'Door closed',
          interruption: 'Interruption',
          'DEVICE HEALTH & CONFIGURATION': 'Device health & configuration',
          'user activity': 'User activity',
          'POWER SUPPLY AND DEVICE COMPONENTS': 'Power supply and device components',
          'component deviation': 'Component deviation',
          'CO2 PARAMETER': 'Co2 parameter',
          'value good': 'Value good',
          'warn low': 'Warn low',
          'PARAMETER CONTROL': 'Parameter control',
          'change of threshold': 'Change of threshold',
          'Program step': 'Program step',
          'change of setpoint': 'Change of setpoint',
          'TEMPERATURE PROBE PARAMETER': 'Temperature probe parameter',
          'component failure': 'Component failure',
          'warn high': 'Warn high',
          'component info': 'Component info',
          'door open too long': 'Door open too long',
          'critical settings': 'Critical settings',
          'general operation': 'General operation',
          'program stop': 'Program stop',
          'AMBIENT TEMPERATURE PARAMETER': 'Ambient temperature parameter',
          'TEMPERATURE PARAMETER': 'Temperature parameter',
          'CABINET TEMPERATURE PARAMETER': 'Cabinet temperature parameter',
          'alert high': 'Alert high',
          'critical high': 'Critical high',
          'critical low': 'Critical low',
          'agitation setpoint changed': 'Agitation setpoint changed',
          'alert low': 'Alert low',
          'program stop/end': 'Program stop/end',
          'program start/step': 'Program start/step',
          'recurring task': 'Recurring task',
          offline: 'Offline',
          'software update': 'Software update',
          'O2 PARAMETER': 'O2 parameter',
          'user management': 'User management',
          'AGITATION PARAMETER': 'Agitation parameter',
          'HUMIDIY PARAMETER': 'Humidity parameter',
          'BRIGHTNESS PARAMETER': 'Brightness parameter',
          'ambient temperature': 'Ambient temperature',
          'BAROMETRIC PARAMETER': 'Barometric parameter',
        },
      },
      overwrite: {
        waterLevel: {
          Ok: 'Normal',
          Low: 'Empty',
          default: 'Unavailable',
        },
        co2Tank: {
          On: 'Filled',
          Error: 'Empty',
          default: 'Check bottle',
          Off: 'Check bottle',
        },
      },
      noLiveData: 'No live data',
      ramps: 'Ramps',
      timer: 'Timer',
      timerStartAt: 'Timer - Start at',
      timerEndAt: 'Timer - End at',
      multiStepSetpointActive: 'Multi step program active',
    },
    auditEvents: {
      generalInformation: 'General information',
      overview: 'Audit trail',
      searchPlaceholder: 'Search audit events',
      header: {
        timestamp: 'Timestamp',
        type: 'Type',
        details: 'Details',
        description: 'Description',
        activity: 'Activity',
        performedBy: 'Performed by',
      },
      notAvailable: 'n/a',
      detailsLabels: {
        user: 'User',
        event: 'Event',
        unknownEvent: 'Unknown event',
        categories: 'Categories',
        severity: 'Severity',
      },
      eventType: {
        device: 'Device',
        notification: 'Notification',
        platform: 'Platform',
        user: 'User',
        deviceEvent: 'Device event',
        unknown: 'Unknown',
      },
      categories: {
        all: 'All',
        device: 'Device',
        event: 'Event',
        notification: 'Notification',
        user: 'User',
        platform: 'Platform',
      },
    },
    alertThresholds: {
      parameterAlert: 'Parameter alerts',
      alertHint:
        'Parameter alerts, setpoint and limits can be set directly on the device. Please look up the device manual for more details.',
      alertHigh: 'Alert high',
      warnHigh: 'Warn high',
      setPoint: 'Set point',
      alertLow: 'Alert low',
      warnLow: 'Warn low',
      unknown: 'Unknown',
      limitsError:
        'Limits must be descending from high to low. <br/>Adjust the parameter values.',
      valueHint: 'Current value',
      valueHintOldTimestamp: 'Value',
      alertThresholdHint: 'Current value within alert range',
      alertThresholdHintOldTimestamp: 'Value within alert range',
      warnThresholdHint: 'Current value within warning range',
      warnThresholdHintOldTimestamp: 'Value within warning range',
      defaultThresholdHint: 'Current value good',
      defaultThresholdHintOldTimestamp: 'Value good',
      loading: 'Saving settings',
      loadingHint: 'Please wait till threshold data gets updated',
    },
    activityStatus: {
      automaticallyRunning: 'Running',
      done: 'Done',
      error: 'Error',
      idle: 'Idle',
      manuallyRunning: 'Running',
      paused: 'Paused',
      offline: 'Unavailable',
      online: 'Running',
      hold: 'Hold',
      running: 'Running',
      unreachable: 'Unreachable',
      preparing: 'Updating',
    },
    notificationsPage: {
      title: 'Notifications',
    },
    incidents: {
      overview: 'Incident history',
      hint: 'View rule-triggered incidents and detailed flow of recipients, acknowledgment or resolution.',
      note: '<bold>Note:</bold><regular> Only C170i and F740 devices support<br/> automatic incident resolution if the situation is<br/> resolved on the device. Incidents on other<br/> devices must be resolved manually.</regular>',
      incidentsStatus: {
        acknowledged: 'Acknowledged',
        escalated_1: 'Escalated',
        escalated_2: 'Escalated',
        resolved: 'Resolved',
        expired: 'Expired',
        actionRequired: 'Action required',
        noActionRequired: 'No action required',
        pending: 'Notification pending',
        unavailable: 'Unavailable',
      },
      incidentsCounter_one: '{{count}} notification',
      incidentsCounter_other: '{{count}} notifications',
      recipients: 'Recipients',
      initialRecipients: 'Initial recipients',
      activeDelay: 'Delay active',
      inActiveDelay: 'Delay',
      triggeredBy: 'Triggered by',
      acknowledgeIncident: 'Acknowledge incident',
      cannotAcknowledgeIncidentHint:
        '<bold>Important:</bold> This incident can only be acknowledged by the rule owner or designated recipients. Contact relevant persons if needed.',
      autoResolvedByDevice: 'auto-resolved by device',
      delayMessage: 'until notifications are sent',
    },
    incidentAcknowledgedDialog: {
      titleSuccess: 'Incident acknowledged',
      titleFailure: 'Acknowledgement failed',
      action: 'You have successfully acknowledged the incident.',
      actionFailed:
        'Please review your settings and try again. Ensure you are connected to the network.',
      descriptionFailed: 'If the problem persists',
      multiRecipientsDescription:
        'Other recipients will be notified you’re managing the issue and no further notifications will be sent.',
      singleRecipientDescription: 'No further notifications will be sent.',
      noRecipientDescription:
        'No notification was and will be sent as incident was acknowledged within the configured delay period for sending.',
      contactSupport: 'consult the VisioNize AI assistant.',
    },
    incidentsTable: {
      noNotificationReceived: {
        title: 'No incidents yet',
        hint: 'Notifications will appear here once your rules are triggered by an event.',
        viewNotificationsRules: 'View notification rules',
      },
      noNotificationsCreated: {
        hint: 'Create a notification rule to get status updates from your devices',
      },
      createdAt: 'Created',
      event: 'Event',
      triggeredBy: 'Triggered by',
      device: 'Device',
      type: 'Type',
    },
    notificationRules: {
      overview: 'Notification rules',
      hint: 'Manage rules for when notifications are triggered to stay informed of key device events.',
      errorHeadline: 'Notification Rule could not be saved',
      add: 'Add notification rule',
      addSuccessHeadline: 'Notification rule added',
      addSuccessMessage:
        'Notification rule <bold>{{name}}</bold> was successfully added.',
      new: 'New notification rule',
      edit: 'Edit notification rule',
      editSuccessHeadline: 'Notification rule updated',
      editSuccessMessage:
        'Notification rule <bold>{{name}}</bold> was successfully updated.',
      editErrorMessage:
        '<p>Please review your setting and try again.</p><p>Ensure you are connected to the network.</p>',
      editSuccessHint:
        '<bold>Please note:</bold> You may still receive messages based on the previous settings while we transition to the new configuration.',
      viewRule: 'View rule',

      addRuleSteps: {
        selectDeviceType: {
          label: 'Select device type',
          fieldDescription:
            'First choose the <bold>device type</bold>, then in the next step you can select either <bold>individual</bold> or <bold>all devices within that type.</bold>',
        },
        chooseDevices: {
          labelPrefix: 'Select',
        },
      },
      inputName: 'Give your notification rule a name',
      defaultNamePrefix: 'Notification rule',
      selectRecipients: 'Select the recipients of this notification rule',
      notificationsChannel: 'Send notifications via',
      active: 'Active',
      paused: 'Paused',
      pause: 'Pause',
      inactive: 'Paused',
      pausedHintDescription:
        'No notifications will be sent based on this rule until it is reactivated.',
      confirmPauseTitle: 'Pause {{name}}?',
      confirmPauseDescription: {
        top: 'If you pause the notification rule:',
        bullet1: 'Notifications based on this rule will not be sent.',
        bullet2: 'You can reactivate the rule at any time.',
      },
      error: {
        nameAlreadyExists: 'Name already exists. Please choose a different one.',
        noDevicesFound: 'No devices found',
      },
      confirmDeleteTitle: 'Delete {{name}}?',
      confirmDeleteDescription: {
        top: 'If you delete the notification rule:',
        bullet1: 'Notifications based on this rule will no longer be sent.',
        bullet2:
          'If applicable, recipients and the rule owner will be emailed about the deletion.',
        bullet3: 'This action cannot be undone.',
      },
      deleteSuccessTitle: 'Notification rule deleted',
      deleteSuccessDescription:
        '<bold>{{name}}</bold> was successfully deleted. It will no longer appear in your rule list or send any notifications.',
      cardFooter: 'If unresolved or unacknowledged after',
      cardNote: '<bold>Important:</bold> The event remains unresolved.',
      subscriptionBannerHeader: 'Up to 3 escalation groups with Sample Safety+',
      subscriptionBannerText:
        'Contact your administrator to upgrade, or <l>reach out to us</l> for assistance.',
      progressIndicator: {
        deviceTypes: 'Device types',
        devices: 'Devices',
        condition: 'Condition',
        recipients: 'Recipients',
        lifecycle: 'Lifecycle',
        name: 'Name',
      },
      lifecycleScreen: {
        hint: 'Select which incident lifecycle events should trigger a notification.',
        highVolumeWarning:
          'This selection may lead to a <bold>high volume</bold> of notifications.',
      },
      notificationTypes: {
        Created: 'Incident created',
        Acknowledged: 'Incident acknowledged',
        AutoResolved: 'Incident resolved',
        Expired: 'Incident expired',
        Archived: 'Notification rule deleted',
      },
      notificationTypesDescriptions: {
        Created: 'Get notified when an incident is created or escalated',
        Acknowledged: 'Get notified when a team member acknowledges an incident',
        AutoResolved: 'Get notified when an incident is resolved',
        Expired: 'Get notified when an incident times out without resolution',
        Archived: 'Get notified when an notification rule is deleted',
      },
      notificationTypesAlwaysEnabled: 'Always enabled',
    },
    notificationRulesTable: {
      name: 'Name',
      tags: 'Event',
      recipients: 'Recipients',
      condition: 'Condition',
      createdBy: 'Created by',
      emptyStateNoPairedDevices:
        'To create a notification rule you first need to add and then pair a device.',
      emptyState:
        'Create a new notification rule to get status updates from your devices.',
      recipientNotice:
        'The notification will be sent to the following recipients via email:',
      tooltip: 'Notification rules define when you get notified by your devices.',
    },
    notificationDetail: {
      notificationRule: 'Notification rule',
      devices: 'Devices',
      recipients: 'Recipients',
      condition: 'Condition',
      lifecycle: 'Lifecycle',
      notificationWillBeSendOn: 'Notification will be sent on:',
      createdBy: 'Created by',
      tryAgain: 'Please try again later',
      noDevicesFound: 'Error loading devices.',
      errorLoadingDevices: 'Error loading some devices.',
      oneGroupEscalationLayerDescription:
        'Group 1 receives the initial notification after the initial delay if configured. If unresolved or unacknowledged after further {{timeOne}}, notifications will stop.',
      twoGroupsEscalationLayerDescription:
        'Group 1 receives the initial notification after the initial delay if configured. If unresolved or unacknowledged after further {{timeOne}}, Group 2 will be notified. Notifications will stop after additional {{timeTwo}}.',
      threeGroupsEscalationLayerDescription:
        'Group 1 receives the initial notification after the initial delay if configured. If unresolved or unacknowledged after further {{timeOne}}, Group 2 will be notified, followed by Group 3 after another {{timeTwo}}. Notifications will stop after additional {{timeThree}}.',
      notificationFrequencyTitle: 'Notifications will be sent every {{value}}',
      notificationDelayTitle: 'Delay {{value}}',
      noDelay: 'No delay',
      tooltipDelay: 'Send notification immediately or after a delay.',
      tooltipViewNoDelay: 'Immediate incident notifications.',
      tooltipView: 'Notifications will be sent after this time.',
    },
    notificationTags: {
      alert: 'Alert: ',
      warning: 'Warning: ',
      error: 'Error: ',
      info: 'Info: ',
      errorMessage:
        'Technical device problem e.g. CO2 concentration process error or firmware update failed',
      alertMessage:
        'Critical situation e.g. door/lid left opened too long, parameter exceeded the alert limits',
      warningMessage:
        'Potentially negative condition e.g. low battery, long door opening - close the door etc.',
      infoMessage:
        'Positive informational event e.g. device reconnected or temperature normalized',
    },
    escalationLayer: {
      initialRecipients: 'Recipient group {{number}}',
      recipients: 'Recipients',
      searchPlaceholder: 'Search by name',
      stopNotifications: 'stop notifications',
      escalate: 'escalate',
      warning: 'Please add recipients to proceed.',
      actionAfter: 'If unresolved or unacknowledged after',
      unresolvedEventLabel: '<bold>Important:</bold> The event may remain unresolved.',
      expiredEventLabel: '<bold>Important:</bold> The event remained unresolved.',
      sendNotificationsEvery: 'Send notifications at least every',
      notificationDelay: 'Delay',
    },
    tagsCondition: {
      notify: "You'll be notified on ",
      events: 'all events',
      within: 'within the ',
      severity: 'selected severity type.',
      select: 'Select condition',
      highVolumeWarning:
        'This selection may lead to a <bold>high volume</bold> of notifications.',
      thresholdsInMonitoring: 'Thresholds in Monitoring',
      modes: {
        advanced: 'Advanced',
        advancedDescription: 'Specific categories',
        basic: 'Basic',
        basicDescription: 'All events by severity',
      },
      advanced: {
        introduction: "You'll be notified only on these specific category conditions.",
        aboutCategory: 'About {{category}}',
        lowerLimit: 'Lower limit',
        upperLimit: 'Upper limit',
        appliesTo: 'Available for {{models}}',
        recommended: 'Recommended',
        recommendedTooltip:
          'Recommended by Eppendorf. Subscribe to receive notifications for events that may require timely attention.',
        unsupportedModels: 'Categories are unavailable for: {{models}}.',
        loading: 'Loading available category conditions…',
        errorTitle: 'Category conditions could not be loaded.',
        retry: 'Try again',
        emptyTitle: 'No category conditions are available for these devices.',
        unsupportedTitle: 'Category conditions are not supported for these devices.',
      },
    },
    userManagement: {
      overview: 'User overview',
      billingUserHint:
        'You are signed in to VisioNize Lab Suite as a <strong>Subscription Manager</strong>. In VisioNize, you can only access the User overview. To manage the VisioNize subscription, go to the <subscriptionLink>Subscription Suite</subscriptionLink>.',
      firstName: 'First name',
      lastName: 'Last name',
      role: 'Role',
      createdAt: 'Created at',
      invitedAt: 'Invited',
      administrator: 'Administrator',
      roleUpdate5MinNotice: 'Role changes may take up to 5 minutes to be effective.',
      contactAdmin: "You can't change your own role, contact an admin to update it.",
      emailNote: "Email is used for verification and can't be edited. ",
      profile: 'Profile',
      status: 'Status',
      resendInvite: 'Resend invite',
      lastUpdated: 'Last updated',
      phoneNumber: 'Mobile number',
      phoneNumberOptional: 'Mobile number (optional)',
      phoneNumberNotVerified: 'Not verified',
      phoneNumberVerified: 'Verified',
      countryCodeSelectPlaceHolder: 'Select a country code',
      limitedSupportedCountriesNotice:
        'Please notice that sms notifications <br/> are only available in certain regions.',
      verifyPhoneNumberNotice:
        'After saving, verify the number to enable SMS notifications.',
      roles: {
        labAdmin: 'Lab Manager',
        labMember: 'Lab Member',
        billingUser: 'Subscription Manager',
      },
    },
    userStatus: {
      pending: 'Invite sent',
      enabled: 'Active',
      disabled: 'Inactive',
      error: 'Error',
    },
    userInvite: {
      inviteUser: 'Invite user',
      inviteSuccess: 'Invite sent',
      inviteResentSuccessfully: 'Invite successfully resent',
      errorMessageUniqueEMail: 'Email already exists.',
      errorMessageEmailMaxLength: 'The email is too long.',
      errorMessageEmailWrongFormat: 'The format of the email address is wrong.',
      descriptionInviteUser: 'Please enter the details of the new user:',
      descriptionInviteConfirmed:
        'Invite was successfully sent to <bold>{{user}}</bold>. Their status will change to <bold>Active</bold> upon profile creation in VisioNize.',
      descriptionEmailMessageCannotBeChanged:
        'The email address can not be changed later.',
      sendInvite: 'Send invite',
    },
    userDelete: {
      deleteSuccess: 'User deleted',
      deleteError: 'Error on user deletion',
      descriptionUserNotDeleted:
        'User <bold>{{user}}</bold> could not be deleted and can still access VisioNize.',
      descriptionUserDeleted:
        'User <bold>{{user}}</bold> has been successfully deleted and cannot access VisioNize anymore. All of the data created by the user will be kept.',
      deleteUserQuestion: 'Delete user?',
      deleteUserAction: 'Delete user',
      deleteUserLongQuestion:
        'Are you sure you want to delete <bold>{{user}}</bold>? This action cannot be undone.',
    },
    userDetail: {
      title: 'User details',
      myProfile: 'My profile',
      preferences: {
        tabTitle: 'Preferences',
        header: 'Communication preferences',
        subHeader:
          'Notification methods for notification rules <bold>(at least one required).</bold>',
        email: 'Email',
        sms: 'SMS',
        messages: {
          common: 'To enable SMS notifications, ',
          ownProfile: {
            enable: 'please <profileLink>add a phone number</profileLink>.',
            verify: 'please <verifyLink>verify your phone number</verifyLink>.',
          },
          otherProfile: {
            enable: 'the user must add a phone number.',
            verify: 'the user must first verify their number.',
          },
        },
        restrictedSmsHeader: 'SMS available with Sample Safety+',
        restrictedSmsText:
          'Contact your administrator to upgrade, or <l>reach out to us</l> for assistance.',
      },
      profile: {
        tabTitle: 'Profile',
      },
    },
    verifyPhoneNumber: {
      title: 'Verify phone',
      termsAndConditionsDialog: {
        title: 'Phone verification',
        verifyPhoneExplanation:
          "Verify your number to receive SMS notifications. We'll send a 4-digit<br/> code to <bold>{{phoneNumber}}</bold> for confirmation.",
        termsAndConditionsCopy:
          'By providing my phone number and checking the box, I agree to receive Device Alarm notifications via SMS from VisioNize Lab Suite. Message frequency varies, Message and data rates may apply{{usDisclaimer}}. I agree to <termsLink>Terms & Conditions</termsLink> and <policyLink>Privacy Policy</policyLink>.',
        sendVerificationButton: 'Send verification code',
      },
      successVerifiedPhoneNumber: {
        title: 'Phone number verified',
        description: 'Verification is successful for <bold>{{phoneNumber}}</bold>.',
        receiveSMSCopy:
          'To receive SMS notifications, enable this option in preferences.',
        preferenceButton: 'Open preferences',
      },
      errorVerifyingPhoneNumber: {
        title: 'Unable to verify phone number',
        description:
          'Please close this dialog and select <bold>Verify number</bold> to retry. <br/> Ensure you are connected to the network',
        errorCopy: 'If the problem persists ',
        contactSupport: 'consult the VisioNize AI assistant.',
      },
      unableToSendVerificationCode: {
        title: 'Unable to send code',
        description:
          'Please close this dialog and select <bold>Verify number</bold> to retry. <br/> Ensure you are connected to the network',
        errorCopy: 'If the problem persists ',
        contactSupport: 'consult the VisioNize AI assistant.',
      },
      verifyPhoneNumberDialog: {
        title: 'Phone verification',
        description: 'We’ve sent a 4-digit code to <bold>{{phoneNumber}}</bold> via SMS.',
        resendCode: "Didn't get the code? <button>Resend</button>",
        resendButton: 'Resend',
        resendCodeTest: "Didn't get the code?",
        verifyButton: 'Verify',
        codeResent: '<p>Code resent. Please check your phone.</p>',
        verificationCodeDoesntMatch: 'Invalid code. Please check and try again.',
      },
    },
    phoneNumberUpdatedDialog: {
      title: 'Phone number updated',
      description:
        'You’ve changed the phone number from <bold>{{fromPhoneNumber}}</bold> to <bold>{{toPhoneNumber}}</bold>.',
      smsNotificationsDisabled:
        'SMS notifications are disabled now. To activate them again, please verify the new number first.',
      enableSMSCommunication:
        'To enable SMS as a communication preference, please verify the new number first.',
    },
    phoneNumberRemovedDialog: {
      title: 'Phone number removed',
      description:
        'You’ve removed the number <bold>{{phoneNumber}}</bold> from your profile.',
      smsNotificationsDisabled:
        'SMS notifications are now disabled. To enable them again, please add and verify a new number.',
      enableSMSCommunication:
        'To enable SMS as a communication preference, please add and verify a new number.',
    },
    osl: {
      title: 'Open source libraries',
      name: 'Name',
      version: 'Version',
      license: 'License',
      url: 'URL',
      repository: 'Repository',
      publisher: 'Publisher',
      visitWebsite: 'Visit website',
      visitRepository: 'Visit repository',
    },
    header: {
      add: 'Add',
    },
    footer: {
      cookieInfo: 'Cookie info',
      cookieSettings: 'Cookie settings',
      termsAndConditions: 'Terms & conditions',
      usepolicy: 'Acceptable use policy',
      privacyPolicy: 'Privacy policy',
      imprint: 'Imprint',
      about: 'About VisioNize',
      oslibraries: 'Open Source Libraries',
      support: 'Support',
    },
    sidebar: {
      dashboard: 'Dashboard',
      deviceManagement: 'Device management',
      monitoring: 'Monitoring',
      reportHub: 'Report hub',
      notifications: 'Notifications',
      userManagement: 'User management',
      auditEvents: 'Audit trail',
      settings: 'Settings',
    },
    organizationSettings: {
      title: 'Settings',
      organization: 'Organization',
      timezone: 'Time zone',
      selectTimezone: 'Select a time zone',
      noTimezoneOptions: 'No time zones found',
      timezoneDescription: 'This time zone is used for reports across your organization.',
      saveError: 'Could not save the organization time zone. Please try again.',
    },
    dashboard: {
      widget: {
        partial: 'Some data could not be loaded. Please try again.',
      },
      title: "Today's lab overview",
      beta: 'Beta',
      betaTooltip:
        'This dashboard is in beta and still under active development. Features may change.',
      greeting: {
        morning: 'Good morning{{firstName}}',
        afternoon: 'Good afternoon{{firstName}}',
        evening: 'Good evening{{firstName}}',
      },
      labHealth: {
        title: 'Overall lab health',
        hint: 'Shows the health of paired devices and how all devices are connected to the lab.',
        description: 'Healthy devices as a share of evaluable paired devices',
        noEvaluableDevices: 'No paired devices have enough status data to evaluate',
        healthy: 'Healthy',
        needsAttention: 'Needs attention',
        unknown: 'Unknown',
        paired: 'Paired devices',
        proposedTitle: 'Proposed: device status',
        connected: 'Connected',
        unavailable: 'Unavailable',
        monitoringHealthTitle: 'Monitoring health',
        devicesNeedAttention: 'devices need attention',
        normalDevices: '{{count}} monitored devices operating normally',
        monitoringAlerts: 'Alerts or errors',
        monitoringWarnings: 'Warnings',
        monitoringUnavailable: 'Unavailable',
        monitoringNoCurrentStatus: 'No current status',
        monitoringAlertsAction: 'Review alerts',
        monitoringWarningsAction: 'Review warnings',
        monitoringUnavailableAction: 'Check connection',
        monitoringNoCurrentStatusAction: 'Review monitoring',
        noActionNeeded: 'No action needed',
        viewAffectedDevices: 'View devices',
        affectedDevicesTitle: '{{status}} devices',
        affectedDevicesDescription: 'Devices with {{status}} status.',
        alertDevicesTitle: 'Devices with alerts',
        warningDevicesTitle: 'Devices with warnings',
        noIssueDevicesTitle: 'Devices with no issues',
        pendingDevicesTitle: 'Pending devices',
        unavailableDevicesTitle: 'Unavailable devices',
        dialogDescription: 'Select a device to view its details.',
        unnamedDevice: 'Unnamed device',
        close: 'Close',
        comparisonTitle: 'Lab health',
        deviceCount: '{{count}} devices',
        openMonitoring: 'Open monitoring',
        monitored: 'Monitored',
        notMonitored: 'Not monitored',
        monitoredWithCount: 'Monitored - {{count}}',
        notMonitoredWithCount: 'Not monitored - {{count}}',
        normal: 'Normal',
        alerts: 'Alerts',
        noIssues: 'No issues',
        alertsDetail: 'Act now',
        warningsDetail: 'Check soon',
        normalDetail: 'No action',
        pendingSetup: 'Pending setup',
        pendingDetail: 'Not yet paired',
        unavailableDetail: 'No data received',
        connectionsTitle: 'Device connections',
        totalDevices: 'Total devices',
        pending: 'Pending',
        readyToPair: 'Ready to pair',
        waitingForMetadata: 'Setting up',
        unclassified: 'Other',
        meterAria: 'Percentage of evaluable paired devices that are healthy',
        viewMonitoring: 'View monitoring',
        viewDevices: 'View devices',
        loadError: 'Overall lab health could not be loaded.',
        connectionsLoadError: 'Device connection counts could not be loaded.',
        deviceStatusLoadError: 'Device status counts could not be loaded.',
        retry: 'Retry',
      },
      deviceOverview: {
        title: 'Favourite devices',
        hint: 'Shows live status and one-hour trends for the devices you selected.',
        emptyTitle: 'No devices selected',
        emptyDescription: 'Select up to three devices to keep their status in view.',
        configure: 'Configure devices',
        deviceLoadError: 'Device status could not be loaded.',
        chartLoadError: 'Parameter history could not be loaded.',
        retry: 'Retry',
        noLocation: 'No location assigned',
        activity: 'Activity',
        alerts: 'Alerts',
        unknownStatus: 'Unknown',
        noParameters: 'No parameters selected',
        lastHour: 'Last hour',
        noHistory: 'No history available for the last hour',
        chartAria: 'One-hour trend for {{parameterName}}',
        warningHigh: 'Warning high',
        warningLow: 'Warning low',
        alertHigh: 'Alert high',
        alertLow: 'Alert low',
        monitoringAction: 'Monitoring',
        monitoringHint: 'View live parameters and device details for {{deviceName}}',
        viewDevice: 'Open monitoring for {{deviceName}}',
      },
      settings: {
        open: 'Configure dashboard',
        title: 'Configure dashboard',
        description: 'Choose the widgets and devices shown on your dashboard.',
        requiresRole: 'Ask your administrator for a role with the required permissions.',
        requiresSubscription: 'Upgrade to Sample Safety Plus to access this widget.',
        requiresEntitlement: 'Report Hub access is required to use this widget.',
        tilesTitle: 'Widgets',
        alwaysOn: 'Always on',
        selectAllWidgets: 'Select all',
        clearAllWidgets: 'Clear all',
        devicesTitle: 'Devices',
        devicesDescription: 'Select up to three paired devices.',
        devicesSelected: '{{selected}} of {{maximum}} selected',
        devicesSelectionLimit: 'You can select up to three devices.',
        searchDevices: 'Search paired devices',
        devicesLoadError: 'Devices could not be loaded.',
        noDevices: 'No devices found',
        resetSearch: 'Reset search',
        previousPage: 'Previous',
        nextPage: 'Next',
        page: 'Page {{page}} of {{totalPages}}',
        parametersTitle: 'Displayed parameters',
        parametersDescription: 'Select up to two chart parameters for each device.',
        parametersLoadError: 'Parameters could not be loaded.',
        noParameters: 'This device has no numeric parameters available.',
        unavailableParameter: '{{telemetryId}} (currently unavailable)',
        removeDevice: 'Remove {{deviceName}} from dashboard',
        retry: 'Retry',
        saveError: 'Dashboard settings could not be saved in this browser.',
        cancel: 'Cancel',
        save: 'Save',
      },
      recentDeviations: {
        title: 'Recent deviations',
        hint: 'Shows raw device warning, alert and error events from the last 24 hours, whether or not they created an incident.',
        period: 'Last 24 hours',
        error: 'Error',
        alert: 'Alert',
        warning: 'Warning',
        resolvedAfter: 'Resolved after {{duration}}',
        ongoingFor: 'Still active for {{duration}}',
        durationUnknown: 'Duration unknown',
        noDeviations: 'No warning, alert or error events in the last 24 hours',
        loadError: 'Recent deviations could not be loaded.',
        retry: 'Retry',
      },
      alertsIncidents: {
        title: 'Alerts & incidents',
        hint: 'Shows notification-rule incidents and their current acknowledgement or resolution state.',
        period: 'Latest incidents',
        error: 'Error',
        alert: 'Alert',
        warning: 'Warning',
        info: 'Info',
        noIncidents: 'No incidents available',
        loadError: 'Alerts and incidents could not be loaded.',
        retry: 'Retry',
      },
      latestAuditTrail: {
        title: 'Latest audit trail',
        hint: 'Shows the latest recorded user and system activity in your lab.',
        noEvents: 'No recent audit events',
        loadError: 'Audit events could not be loaded.',
        retry: 'Retry',
      },
      pendingInvitations: {
        title: 'Pending invitations',
        hint: 'Shows users who were invited but have not completed account setup yet.',
        noInvitations: 'No pending invitations',
        inviteSent: 'Invite sent',
        loadError: 'Pending invitations could not be loaded.',
        retry: 'Retry',
      },
      latestReports: {
        title: 'Latest reports',
        hint: 'Shows the most recently generated stability and audit trail reports.',
        noReports: 'No reports available',
        download: 'Download',
        loadError: 'Reports could not be loaded.',
        downloadError: 'The report could not be downloaded. Please try again.',
        retry: 'Retry',
      },
      quickActions: {
        title: 'Quick actions',
        hint: 'Provides shortcuts for common dashboard tasks available to your role.',
        createNotificationRule: 'Create notification rule',
        createReport: 'Create report',
      },
      feedback: {
        saveError: 'Feedback could not be sent. Please try again.',
        useful: 'Useful',
        notUseful: 'Not useful',
        likeAria: 'Like this section',
        dislikeAria: 'Dislike this section',
      },
    },
    pagination: {
      pageSize: 'Rows per page',
      itemRange: '{{startItem}} - {{endItem}} of {{totalItems}} items',
    },
    landing: {
      title: 'Welcome to the new VisioNize',
      symbol: '®',
      labSuiteExperienceText: 'Lab Suite experience',
      signInHint: 'If you already have an account continue to the sign in page',
      signUpHint: 'No account yet? Get started for free!',
      signUpButton: 'Sign up',
      signInButton: 'Sign in',
      createNewAccountHint: 'Create new account',
      setUpAccountHint: 'Please continue to set up your account.',
      setUpButton: 'Set up account',
      policyHint: 'Before using our product, please agree to the',
      policyLink: 'Acceptable Use Policy',
    },
    subscription: {
      manageSubscription: 'Subscription Suite',
      needMoreDevicesTitle: 'Need more devices?',
      needMoreDevicesText:
        'Licenses are managed by the subscription owner in the Subscription Suite (external site). Please contact them for assistance.',
      limitedOfferTitle: 'Limited offer - {{daysRemaining}} days remaining',
      limitedOffer: {
        text: 'Full access to <bold>Sample Safety+</bold> features (Data access for 90 days, SMS notifications, Response-based alert escalation and Audit trail) until {{endDate}}.',
        helpText:
          "If you don't know who is managing your account,<br>please <l>reach out to us</l>.",
        button: 'Upgrade now',
      },
      features: {
        realTimeMonitoring: 'Real-time monitoring',
        emailNotifications: 'Email and SMS notifications',
        dataAccess: 'Data access for {{days}} days',
        deviceDataExport: 'Device data export',
        alertEscalation: 'Response-based alert escalation',
        auditTrail: 'Audit trail',
      },
      deviceUsage: {
        title: 'Device usage',
        overLimit: '{{amount}} over limit',
        countExplanation:
          'VisioNize gateways & boxes are not counted as connected devices.',
        connected: 'Connected',
        licensed: 'Licensed',
      },
      auditTrailUpgrade: {
        title: 'Unlock enhanced compliance and traceability tools',
        badge: 'Sample Safety+',
        text: 'Audit trail supports your compliance needs with even more <bold>comprehensive lab documentation</bold>. Track user interactions or alert acknowledgement with time-stamped data in one central audit log available in the <bold>Sample Safety+</bold> subscription.',
        deviceHistory:
          '<bold>Comprehensive device history</bold>: Track every device event—errors, updates, and access.',
        notificationAuditing:
          '<bold>Notification auditing</bold>: Monitor all notification rules, sent alerts, and user acknowledgments.',
        userAccountability:
          '<bold>User accountability</bold>: See exactly who accessed devices, made changes, or performed critical actions (when user management is activated on the device).',
        gainFullAccess:
          'Gain full access to <bold>Sample Safety+</bold> features (Data access for 90 days, SMS notifications, Response-based alert escalation and Audit trail).',
        helpText:
          "If you don't know who is managing your account, please <l>reach out to us</l>.",
        button: 'Upgrade now',
      },
      reportHubUpgrade: {
        title: 'Keep your device data together in one place',
        badge: 'Beta | Sample Safety+',
        description:
          'The Report hub keeps your device records together in one place. Store read-only records and access them whenever you need them. Review weeks of freezer or incubator data in exportable stability reports. Document every run of a mixer or centrifuge with a run record. All records are read-only and available as PDF/A-2a files for download. PDF/A-2a is a file format designed for long-term archiving.',
        auditTrailExport:
          '<bold>Audit trail export</bold>: Export a file-based record of your audit trail for a period you select.',
        runRecords:
          '<bold>Run records</bold>: Automatically create a record for each run of a Eppendorf ThermoMixer C. Each record includes key documentation, such as program settings and temperature and speed charts.',
        stabilityReports:
          '<bold>Stability reports</bold>: Review device performance over time. Stability reports include warning and alert periods, events, and charts for all recorded parameters.',
        beta: {
          title: 'Report hub is currently in beta',
          description:
            'You are getting early access to a new feature for stability and audit trail reports. We would love your feedback on how it works, what you like, and what you feel is missing. Your input helps us shape the final version.',
        },
        gainFullAccess:
          'Gain full access to <bold>Sample Safety+</bold> features (Data access for 90 days, SMS notifications, Response-based alert escalation and Audit trail).',
        helpText:
          "If you don't know who is managing your account, please <l>reach out to us</l>.",
        button: 'Upgrade now',
      },
      reportHubBetaAccess: {
        badge: 'Beta',
        notice:
          'The beta phase runs <bold>until the beginning of 2027</bold>. During this time, we may update, move, or discontinue the feature. We plan to launch it officially, but we can’t guarantee that it will return in its current form or at the same price.',
        button: 'Join for free',
      },
    },
    reportHub: {
      title: 'Report hub',
      stabilityReport: 'Stability report',
      tabs: {
        auditTrail: 'Audit trail',
        runRecords: 'Run records',
        stability: 'Stability',
      },
      accessDenied: 'Only Lab Managers can access this page.',
      search: {
        placeholder: 'Search report',
      },
      filters: {
        all: 'All',
        info: 'Info',
        warning: 'Warning',
        ready: 'Ready',
        pending: 'Queued',
        processing: 'Generating',
        error: 'Error',
        alert: 'Alert',
      },
      table: {
        name: 'Name',
        device: 'Device',
        programName: 'Program name',
        reportPeriod: 'Report period',
        runOutcome: 'Run outcome',
        start: 'Start',
        end: 'End',
        startedBy: 'Started by {{user}}',
        started: 'Started',
        endedBy: 'Finished by {{user}}',
        ended: 'Finished',
        requestedBy: 'Requested by',
        generated: 'Generated',
        sha256Hash: 'SHA-256 Hash',
        fileSize: 'File size',
        download: 'Download',
        standard: 'Standard',
        runTypes: {
          MultiStep: 'Multi step',
          SingleStep: 'Single step',
          ShortSpin: 'Short spin',
          FastTemp: 'Fast temp',
        },
        runOutcomes: {
          CompletedWithWarnings: 'Completed with warnings',
        },
      },
      status: {
        ready: 'Ready',
        queued: 'Queued',
        generating: 'Generating',
        error: 'Error',
      },
      emptyState: {
        stability: {
          title: 'No stability reports yet',
          description:
            'Create stability reports from the Monitoring view for selected devices. Once generated, they will appear here.',
          action: 'Go to monitoring',
        },
        auditTrail: {
          title: 'No audit trail reports yet',
          description:
            'Export audit trail reports from the Audit trail view. Once generated, they will appear here.',
          action: 'Go to audit trail',
        },
        runRecords: {
          title: 'No run records yet',
          description:
            'Create run records on supported devices. Once generated, they will appear here.',
          action: 'Go to devices',
        },
      },
      deleteConfirm: {
        title: 'Delete report',
        deletedTitle: 'Report deleted',
        message: 'Report was successfully deleted.',
        prompt: 'Are you sure you want to delete this report?',
        description: 'This action cannot be undone.',
        confirm: 'Delete',
      },
      beta: {
        badge: 'Beta',
        dialogTitle: 'Report hub is currently in beta',
        dialogDescription:
          'You are getting <bold>early access</bold> to a new feature for audit trail exports, run records, and stability reports.',
        dialogFeedbackIntro:
          'We would love your feedback on how it works, what you like, and what you feel is missing. Your input helps us shape the final version.',
        dialogBookCallLabel: 'Open for a 30-minute call with our Product team?',
        dialogBookCallButton: 'Book a time',
        dialogShareFeedbackLabel: 'Prefer to share feedback by email or in writing?',
        dialogShareFeedbackButton: 'Share feedback',
        dialogNotice:
          'The beta phase runs <bold>until the beginning of 2027</bold>. After that, the feature might be updated, moved, or discontinued. While we hope to launch it officially, we cannot guarantee it will return in the same form or price.',
      },
    },
    stabilityDetail: {
      assessment: {
        good: 'Good',
        alert: 'Alert',
        warning: 'Warning',
        offline: 'Device offline',
        'no-data': 'No recorded data',
        'alerts-disabled': 'Alerts disabled',
        'thresholds-unavailable': 'Thresholds unavailable',
        'history-unavailable': 'History unavailable',
      },
      sections: {
        historyCharts: 'History charts',
        events: 'Events',
        notes: 'Notes',
      },
      actions: {
        delete: 'Delete',
        download: 'Download',
        downloadReportAriaLabel: 'Download report',
      },
      metadata: {
        requestedBy: 'Requested by',
        on: 'on',
        sha256Hash: 'SHA-256 Hash',
      },
      door: {
        title: 'Door',
        totalOpenings: 'Total openings',
        totalOpenTime: 'Total open time',
        averageOpenDuration: 'Average open duration',
      },
      parameterStats: {
        setpoint: 'Setpoint',
        average: 'Average',
        minimum: 'Minimum',
        maximum: 'Maximum',
      },
      timeInRanges: {
        title: 'Time in ranges',
        labels: {
          good: 'Good',
          alert: 'Alert',
          warning: 'Warning',
          unknown: 'Unknown',
        },
      },
      setpointChanges: {
        changesCount: '{{count}} changes',
        titleWithCount: 'Setpoint changes ({{count}})',
      },
      noParametersAvailable: 'No parameters available.',
      noEventsWithinTimeRange: 'No events within time range.',
    },
    runRecordDetail: {
      title: 'Run record',
      sections: {
        historyCharts: 'History charts',
        events: 'Events',
      },
      actions: {
        delete: 'Delete',
        download: 'Download',
      },
      metadata: {
        sha256Hash: 'SHA-256 Hash',
        size: 'Size',
      },
      info: {
        initialParameters: 'Initial parameters',
        timer: 'Timer',
        stepN: 'Step {{index}}',
        stepCount_one: '{{count}} step',
        stepCount_other: '{{count}} steps',
        radius: 'Radius',
        timeCounting: 'Time counting',
        ramps: 'Ramps',
        rotor: 'Rotor',
      },
      charts: {
        noChartsAvailable: 'No charts available.',
        notReadyYet:
          'Charts for this run record are not ready yet. They will appear here once processing finishes.',
      },
      events: {
        notReadyYet:
          'Events for this run record are not ready yet. They will appear here once processing finishes.',
      },
    },
    chatbot: {
      title: 'VisioNize support chat',
      messagePlaceholder: 'Your message',
      toggleLabel: 'Ask assistant',
      openTitle: 'Ask assistant',
      closeTitle: 'Close assistant',
      fullView: 'Full view',
      exitFullView: 'Exit full view',
      initialBotMessage:
        'I am the VisioNize AI Assistant. I can help you with questions about using VisioNize Lab Suite and your connected devices. My curated knowledge base is constantly updated by Eppendorf, and I am still learning. If I am not able to provide an answer for you, please come back in the future. Ensure to always use your devices in compliance with the Operating manuals issued by Eppendorf.',
      initialMessageFurtherHelp:
        'If you need further assistance, you can contact the <l>VisioNize support</l>.',
      connectionError:
        "I'm sorry, I'm having trouble connecting right now. Please try again later.",
      consentHeader: 'VisioNize support chat',
      consentText:
        'Our AI assistant can help you with troubleshooting and usage questions. It uses a curated knowledge base which is continuously maintained and extended by Eppendorf.',
      consentTextSubText: 'Please consider the following before continuing:',
      consentBullet1:
        'Your conversation is stored anonymously and is solely used to improve the chat experience.',
      consentBullet2:
        'Your conversation and your data is always handled in compliance with our <l>privacy policy</l>.',
      consentBullet3:
        'It is not a human. If you need further assistance, you can contact the <l>VisioNize support</l>.',
      consentAccept: 'Continue',
      consentDecline: 'Cancel',
    },
  },
};
